import { Injectable } from '@angular/core';

import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/toPromise';
import 'rxjs/add/observable/zip';

import * as firebase from 'firebase/app';

import { AngularFireAuth } from 'angularfire2/auth';
import {
  AngularFireDatabase,
  FirebaseObjectObservable,
  FirebaseListObservable
} from 'angularfire2/database';

import * as _ from 'lodash';
import * as Pluralize from 'pluralize';

import { ParseDescription, ParseDept } from '../item-parser';

// import dictionaries
import {
  StoreDepts,
  StoreDeptOrder,
  Conversions,
  MeasurementTypes
} from '../dictionaries';

// import models
import { User } from '../models/user';
import { Collaborator } from '../models/collaborator';
import { Item } from '../models/item';
import { List } from '../models/list';
import { LastList } from '../models/last-list';
import { SharedList } from '../models/shared-list';
import { Recipe } from '../models/recipe';
import { Preferences } from '../models/preferences';
import { Feedback } from '../models/feedback';

@Injectable()
export class FirebaseService {
  firebaseUser     : Observable<firebase.User>;
  signedIn         : Observable<boolean>;
  signedOut        : Observable<boolean>;
  uid              : Observable<string>;
  user             : FirebaseObjectObservable<User>;
  lastList         : FirebaseObjectObservable<LastList>;
  preferences      : FirebaseObjectObservable<Preferences>;
  lists            : FirebaseListObservable<List[]>;
  sharedLists      : Observable<any>;
  items            : FirebaseListObservable<any[]>;
  listItems        : FirebaseListObservable<Item[]>;
  listItemsByGroup : Observable<any>;
  recipes          : FirebaseListObservable<Recipe[]>;
  recipeItems      : FirebaseListObservable<Item[]>;

  // default item sort is by date added
  defaultSortSubject : string = 'createdAt';
  itemsSortSubject   : BehaviorSubject<string> = new BehaviorSubject<string>(this.defaultSortSubject);

  constructor(
    private afAuth : AngularFireAuth,
    private db     : AngularFireDatabase
  ) {
    this.firebaseUser = this.afAuth.authState;
    this.signedIn     = this.afAuth.authState.map(user => !!user);
    this.signedOut    = this.afAuth.authState.map(user => !user);
    this.uid          = this.afAuth.authState.map(user => user != null ? user.uid : null);

    this.uid.subscribe(uid => {
      if (uid) {
        this.user        = this.db.object(`/users/${uid}`);
        this.lastList    = this.db.object(`/users/${uid}/lastList`);
        this.preferences = this.db.object(`/users/${uid}/preferences`);
        this.lists       = this.db.list(`/lists/${uid}`);
        this.recipes     = this.db.list(`/recipes/${uid}`);
        this.items       = this.db.list(`/items/${uid}`);

        this.getSharedLists(uid);
      } else {
        // console.log('user is not signedIn');
      }
    });
  }

  // auth services
  emailSignInUp(email : string, password : string, isSignup : boolean) : firebase.Promise<any> {
    return this.afAuth.auth
      [`${ isSignup ? 'createUser' : 'signIn' }WithEmailAndPassword`](email, password)
      .then(value => {
        // console.log(`Nice, email sign ${ isSignup ? 'up' : 'in' } worked!`, value);

        return this.checkNewUser(value)
          .then(response => Promise.resolve(response.user));
      }, err => {
        // console.log('Something went wrong:', err.message);

        // if user has correct email address but wrong password
        // perhaps they signed up/in via Google
        // check so we can provide helpful error messaging
        if (err && err.code === 'auth/wrong-password')
          return this.afAuth.auth.fetchProvidersForEmail(email)
            .then(providers => {
              const custErr = { code : 'auth/wrong-provider' };

              return Promise.resolve(providers[0] !== 'password' ? custErr : err);
            });
        else
          return Promise.resolve(err);
      });
  }

  providerLogin(isGoogle : boolean = true) : firebase.Promise<any> {
    const providerName = isGoogle ? 'Google' : 'Facebook';

    return this.afAuth.auth
      .signInWithPopup(new firebase.auth[`${providerName}AuthProvider`]())
      .then(value => {
        // console.log(`Nice, ${providerName} sign in worked!`, value);

        return this.checkNewUser(value.user)
          .then(response => {
            if (!response.isNewUser) {
              // update user's profile photo
              // in case it's changed
              let changes = { profilePhoto : value.user.providerData[0].photoURL };

              // if signing in with Google/FB for the first time
              // update user's displayName and auth provider
              if (response.user.authProvider !== value.user.providerData[0].providerId) {
                changes['displayName']  = value.user.providerData[0].displayName || null;
                changes['authProvider'] = value.user.providerData[0].providerId;
              }

              this.updateUser(changes);
            }

            return Promise.resolve(response.user);
          });
      }, err => {
        // console.log('Something went wrong:', err);

        return Promise.resolve(err);
      });
  }

  checkNewUser(userInfo) : firebase.Promise<any> {
    this.user = this.db.object(`/users/${userInfo.uid}`);

    return this.user.$ref.once('value')
      .then(snapshot => {
        const isNewUser = !snapshot.exists();

        if (isNewUser)
          return this.createUser(userInfo)
            .then(newUser => Promise.resolve({
              user : newUser,
              isNewUser
            }));
        else
          return this.updateUser()
            .then(() => this.user
              .take(1)
              .toPromise()
              .then(unwrappedUser => Promise.resolve({
                user : unwrappedUser,
                isNewUser
              })));
      });
  }

  createUser(userInfo) : firebase.Promise<any> {
    return this.createFirstList(userInfo.uid) // create first list
      .then(firstList => {
        const timestamp = firebase.database.ServerValue.TIMESTAMP,
          newUser : User = {
            createdAt       : timestamp,
            lastLogin       : timestamp,
            email           : userInfo.email,
            emailNormalized : userInfo.email.toLowerCase(),
            authProvider    : userInfo.providerData[0].providerId,
            displayName     : userInfo.displayName || null,
            profilePhoto    : userInfo.photoURL,
            lastList        : {
              listID : firstList,
              type   : 'list'
            },
            preferences     : {
              abbreviations : true,
              fractions     : true
            }
          };

        return this.updateUser(newUser) // save user info
          .then(() => Promise.resolve(newUser));
      });
  }

  updateUser(changes = {}) : firebase.Promise<any> {
    if (changes == null || !changes.hasOwnProperty('lastLogin'))
      changes['lastLogin'] = firebase.database.ServerValue.TIMESTAMP;

    return this.user
      .update(changes);
  }

  logout() : firebase.Promise<any> {
    return this.afAuth.auth
      .signOut();
  }


  // user services
  queryUserByEmail(email : string) : Observable<any> {
    return this.db
      .list('/users', { query : {
        orderByChild: 'emailNormalized',
        equalTo: email.toLowerCase()
      }})
      .map(data => data[0]);
  }


  // item services
  updateItems(uid : string, listID : string) : FirebaseListObservable<any[]> {
    return this.listItems = this.db
      .list(`/items/${uid}/${listID}`, { query : {
        // orderByChild: 'createdAt'
        // orderByChild: 'item'
        // orderByChild: 'dept'
        // orderByChild: 'recipe'
        orderByChild : this.itemsSortSubject
      }});
  }

  displayItems() : Observable<any> {
    return this.listItems
      .map(this.shunTheDamned);
  }

  checkGroupSort() : void {
    // check to see if group sort
    this.itemsSortSubject
      .subscribe(subject => {
        if (subject === 'dept' || subject === 'recipe')
          this.updateGroupedListItems(subject);
      });
  }

  // handle list items divided by group
  updateGroupedListItems(subject : string) {
    this.listItemsByGroup = this.listItems
      .map(this.shunTheDamned)
      .map(items => this.sortItemsIntoGroups(subject, items));
  }

  sortItemsIntoGroups(subject : string, items : Array<any>) : Array<object> {
    let itemsByGroup = {},
      sortedItems = [],
      sortOrder : string[];

    for (let item of items) {
      const itemGroup = item[subject] || 'unknown';

      if (!itemsByGroup[itemGroup])
        itemsByGroup[itemGroup] = [];

      itemsByGroup[itemGroup].push(item);
    }

    if (subject === 'dept')
      sortOrder = StoreDeptOrder;
    else
      // ensure "unknown" is last in list
      sortOrder = this.backOfTheLine(_.keys(itemsByGroup), 'unknown');

    for (let group of sortOrder) {
      if (itemsByGroup[group])
        sortedItems.push({
          name  : this.getGroupName(subject, group),
          items : itemsByGroup[group]
        });
    }

    return sortedItems;
  }

  // move specific item of array to end of the array
  backOfTheLine(haystack : Array<any>, needle) : Array<any> {
    haystack.push(haystack.splice(haystack.indexOf(needle), 1)[0]);

    return haystack;
  }

  getGroupName(subject : string, group : string) : string {
    let name : string;

    if (subject === 'dept')
      name = StoreDepts[group];
    else
      name = group === 'unknown' ? 'No Recipe' : group;

    return name;
  }

  combineLikeItems() : void {
    this.listItems
      .take(1)
      .subscribe(items => {
        let parentCombinedObj = {},
          firstObj            = {},
          newMergedObj        = {},
          nonMergedArr        = [];

        // first, pull out existing merged items
        for (const item of items) {
          if (item.combinedItem)
            parentCombinedObj[item.combinedItem] = item;
          else if (!item.combined)
            nonMergedArr.push(item);
        }

        for (const item of nonMergedArr) {
          // clean item:
          // singularize, remove accent marks, make lowercase, and remove any other non-word symbols
          const cleanItem = Pluralize.singular(_.deburr(item.item.toLowerCase()).replace(/[^\w\-\s]/g, '')),
            key = cleanItem + item.unitType + (item.unitType === 'quantity' && item.unit ? item.unit : '');

          if (parentCombinedObj[key]) // check if parent combined item already exists
            // add this new item to existing combined item
            this.addToExistingCombinedItem(item, parentCombinedObj[key]);
          else if (!firstObj[key]) // check if this is 1st item of it's type
            firstObj[key] = item;
          else if (newMergedObj[key]) // check if this is 3rd item of it's type
            newMergedObj[key].combinedItems.push(item);
          else // otherwise, this is the 2nd item of it's type and can be combined!
            newMergedObj[key] = {
              item : {
                createdAt     : item.createdAt,
                item          : cleanItem,
                lowercaseItem : cleanItem,
                qty           : 0,
                scale         : firstObj[key].scale,
                unit          : firstObj[key].unit || null,
                unitType      : item.unitType,
                completed     : true,
                dept          : item.dept || null,
                recipe        : item.recipe || null,
                combinedItem  : key
              },
              combinedItems : [
                firstObj[key],
                item
              ]
            };
        }

        for (const merged in newMergedObj) {
          for (const item of newMergedObj[merged].combinedItems) {
            // add up merged quantities
            const newMergedItem = newMergedObj[merged].item,
              qty = newMergedObj[merged].item.unitType === 'quantity' ? item.qty : this.convertMeasurement(
                item.unit,
                newMergedObj[merged].item.unit,
                item.qty
              );

            newMergedObj[merged].item.qty += qty;

            // if all merged items don't share same scale, set to default, 1
            if (newMergedObj[merged].item.scale !== item.scale)
              newMergedObj[merged].item.scale = 1;

            // set merged item to complete only if all merged items are complete
            if (newMergedObj[merged].item.completed && !item.completed)
              newMergedObj[merged].item.completed = false;

            // set recipe on merged item only if all merged items share same recipe
            if (newMergedObj[merged].item.recipe && newMergedObj[merged].item.recipe != item.recipe)
              newMergedObj[merged].item.recipe = null;

            // set createdAt to lowest of combined items
            if (newMergedObj[merged].item.createdAt > item.createdAt)
              newMergedObj[merged].item.createdAt = item.createdAt;
          }

          // add merged item to items
          this.createItem(newMergedObj[merged].item)
            .then(newItem => {
              for (const item of newMergedObj[merged].combinedItems) {
                // mark item as combined
                this.updateItem(item, { combined : newItem.key });
              }
            });
        }
      });
  }

  addToExistingCombinedItem(newItem : Item, combinedItem : Item) : void {
    // add up merged quantities
    const qty = newItem.unitType === 'quantity' ? newItem.qty : this.convertMeasurement(
        newItem.unit,
        combinedItem.unit,
        newItem.qty
      );

    // increment combined qty
    this.updateItem(combinedItem, {
      qty : combinedItem.qty + qty
    });

    // mark new item as combined
    this.updateItem(newItem, {
      combined : combinedItem.$key
    });
  }

  sortItemsBy(subject : string) : Promise<boolean> {
    this.itemsSortSubject.next(subject);

    return Promise.resolve(true);
  }


  // list services
  getListRecipe(uid : string, key : string, isList : boolean = true) : FirebaseObjectObservable<List> {
    const type = isList ? 'list' : 'recipe',
      list = this.db
        .object(`/${type}s/${uid}/${key}`);

    list
      .take(1)
      .subscribe(data => {
        this.updateItems(uid, key);

        this.getListSort(uid, key)
          .then(sort => this.sortItemsBy(sort ? sort.name : this.defaultSortSubject)
            .then(() => this.checkGroupSort()));

        this.updateLastList({
          listID : key,
          type
        });
      });

    return list;
  }

  getListSort(uid : string, listID : string) : firebase.Promise<any> {
    const listSort = this.db.object(`/sorting/${uid}/${listID}`);

    return listSort.$ref.once('value')
      .then(snapshot => {
        return snapshot.exists() ? snapshot.val() : false;
      });
  }

  updateListSort(uid : string, listID : string, sort : string) : firebase.Promise<any> {
    const sortData = {
      name      : sort,
      createdAt : firebase.database.ServerValue.TIMESTAMP
    };

    return this.db.object(`/sorting/${uid}/${listID}`).update(sortData);
  }

  createListRecipe(name : string, isList = true) : firebase.Promise<any> {
    const timestamp : object = firebase.database.ServerValue.TIMESTAMP,
      item : List|Recipe = {
        name        : name,
        createdAt   : timestamp,
        lastUpdated : timestamp
      };

    return this[isList ? 'lists' : 'recipes'].push(item)
      .then(newItem => Promise.resolve(newItem.key));
  }

  createFirstList(uid : string) : firebase.Promise<any> {
    return this.createListRecipe('First List');
  }

  updateListRecipe(isList : boolean, item : List | Recipe, changes) : firebase.Promise<any> {
    if (!changes.lastUpdated)
      changes.lastUpdated = firebase.database.ServerValue.TIMESTAMP;

    return this[isList ? 'lists' : 'recipes'].update(item.$key, changes);
  }

  updateLastList(changes : object) : firebase.Promise<any> {
    return this.lastList
      .update(changes);
  }

  updatePreferences(changes : object) : firebase.Promise<any> {
    return this.preferences
      .update(changes);
  }

  removeListRecipe(isList : boolean, item : List | Recipe) : firebase.Promise<any> {
    if (item == null || item.$key == null) return;

    return this[isList ? 'lists' : 'recipes'].remove(item.$key)
      .then(() => {
        this.uid
          .take(1)
          .subscribe(uid => this.db.object(`/items/${uid}/${item.$key}`).remove());

        if (isList && item.shared)
          this.removeSharingFromDeletedList(item.$key)
      });
  }

  removeSharingFromDeletedList(listID : string) : Promise<boolean> {
    const listRef = this.db.list(`/sharing/lists/${listID}`);

    listRef
      .take(1)
      .subscribe(collabs => {
        for (const collab of collabs) {
          this.removeCollaborator(listID, collab.$key, listRef);
        }
      });

    return Promise.resolve(true);
  }


  // shared list services
  getSharedLists(uid : string) : void {
    this.sharedLists = this.db
      .list(`/sharing/users/${uid}`)
      .map(sharedLists => {
        for (let sharedList of sharedLists) {
          // sharedList.name = this.db
          //   .object(`/lists/${sharedList.owner}/${sharedList.$key}`)
          //     .map(list => list.name);

          this.db.object(`/lists/${sharedList.owner}/${sharedList.$key}`)
            .take(1)
            .subscribe(list => sharedList.name = list.name);

          // find each corresponding owner name and store it as an Observable
          sharedList.ownerName = this.db
            .object(`/users/${sharedList.owner}`)
              .map(user => user.displayName || user.email);
        }

        return sharedLists;
      });
  }

  displaySharedLists(sort : string, ascending : boolean = true) : Observable<any[]> {
    return this.sharedLists
      .map(data => {
        if (sort === 'name' || sort === 'normalizedName') {
          sort = 'normalizedName';

          for (const sharedList of data) {
            sharedList[sort] = sharedList.name ? sharedList.name.toLowerCase() : '';
          }
        }

        data = _.sortBy(data, [sort]);

        if (!ascending)
          data.reverse();

        return data;
      });
  }

  getSharedList(uid : string, key : string) : Observable<any> {
    let listOwner : string;

    const list = this.db
      .object(`/sharing/users/${uid}/${key}`)
      .take(1)
      .map(sharedList => {
        listOwner = sharedList.owner;

        sharedList.list = this.db
          .object(`/lists/${listOwner}/${key}`);

        sharedList.ownerName = this.db
          .object(`/users/${listOwner}`)
            .map(user => user.displayName || user.email);

        return sharedList;
      });

    list
      .take(1)
      .subscribe(data => {
        if (!!listOwner && !!data.list && !!data.$key) {
          this.updateItems(listOwner, data.$key);

          this.getListSort(uid, data.$key)
            .then(sort => this.sortItemsBy(sort ? sort.name : this.defaultSortSubject)
            .then(() => this.checkGroupSort()));

          this.updateLastList({
            listID : data.$key,
            type   : 'shared-list'
          });
        }
      });

    return list;
  }

  getCollaborators(listID : string) : Observable<any> {
    return this.db
      .list(`/sharing/lists/${listID}`)
      .map(this.shunTheDamned)
      .map(collaborators => {
        for (const collaborator of collaborators) {
          collaborator.data = this.db
            .object(`/users/${collaborator.$key}`);
        }

        return collaborators;
      });
  }

  addCollaborator(listID : string, email : string) : Observable<any> {
    return Observable
      .zip(
        this.uid,
        this.queryUserByEmail(email)
      )
      .take(1)
      .map(([uid, user]) => {
        if (user) {
          const timestamp = firebase.database.ServerValue.TIMESTAMP,
            sharedList : SharedList = {
              createdAt : timestamp,
              owner     : uid
            },
            collaborator : Collaborator = {
              createdAt : timestamp
            };

          // add shared list to collaborator
          this.db.object(`/sharing/users/${user.$key}/${listID}`)
            .set(sharedList);

          // add collaborator to shared list
          this.db.object(`/sharing/lists/${listID}/${user.$key}`)
            .set(collaborator);

          this.toggleSharedList(listID, true);

          return {
            success      : true,
            collaborator : {
              uid : user.$key,
              email
            }
          };
        } else {
          return {
            success      : false,
            collaborator : {
              email
            }
          };
        }
      });
  }

  updateCollaborator(listID : string, collaboratorID : string, changes) : firebase.Promise<any> {
    return this.db.object(`/sharing/lists/${listID}/${collaboratorID}`)
      .update(changes);
  }

  removeCollaborator(listID : string, collaboratorID : string, listRef? : FirebaseListObservable<Collaborator[]>) : firebase.Promise<any> {
    const collabRef = this.db.object(`/sharing/users/${collaboratorID}/${listID}`);

    if (!listRef)
      listRef = this.db.list(`/sharing/lists/${listID}`);

    collabRef
      .take(1)
      .subscribe(list => {
        collabRef.remove()
          .then(() => {
            listRef.remove(collaboratorID)
              .then(() => {
                return Promise.all([
                  listRef.$ref.once('value'),
                  this.db.object(`/lists/${list.owner}/${listID}`).$ref.once('value')
                ])
                .then(([rawSharedList, rawList]) => {
                  if (!rawSharedList.exists() && rawList.exists())
                    this.toggleSharedList(listID, false);
                });
              });
          });
      });

    return Promise.resolve(true);
  }

  toggleSharedList(listID : string, shared : boolean) : firebase.Promise<void> {
    return this.lists.update(listID, { shared : shared });
  }


  // item services
  createItemFromDescription(description : string, recipe? : string, saveToRecipe? : boolean, throttleCombine? : boolean) : firebase.Promise<any> {
    let item : Item = {
      originalDescription : description,
      completed           : false,
      createdAt           : null,
      lastUpdated         : null,
      qty                 : null,
      scale               : 1,
      lowercaseItem       : null,
      unitType            : null,
      item                : null,
      dept                : null,
      recipe              : recipe && recipe !== '' ? recipe : null
    };

    // extend item with parsed description info
    _.assign(item, ParseDescription(description));

    return this.createItem(item, item.recipe && saveToRecipe, throttleCombine);
  }

  createItem(item : Item, saveToRecipe? : boolean, throttleCombine? : boolean) : firebase.Promise<any> {
    const timestamp = firebase.database.ServerValue.TIMESTAMP;

    if (!item.createdAt)
      item.createdAt = timestamp;

    item.lastUpdated = timestamp;

    return this.listItems.push(item)
      .then(newItem => {
        // check to see if items are combineable
        // unless this is a combined item
        // or combine check is delayed
        if (!(item.combinedItem || throttleCombine))
          this.combineLikeItems();

        // if saving a new recipe, add to recipe list
        if (saveToRecipe)
          this.recipeItems.push(item);

        return Promise.resolve(newItem);
      });
  }

  createBulkItems(items : string, recipe? : string, saveToRecipe? : boolean) : firebase.Promise<any> {
    const newArr = items.split(/\n/);

    newArr.forEach((newItem, index) => {
      if (newItem !== '')
        this.createItemFromDescription(newItem, recipe, !!saveToRecipe, index + 1 !== newArr.length);
    });

    return Promise.resolve(true);
  }

  createBulkItemsWithRecipe(uid : string, items : string, recipe : string) : void {
    this.queryOrCreateRecipe(uid, recipe)
      .then(recipeID => {
        this.recipeItems = this.db.list(`/items/${uid}/${recipeID}`);
      })
      .then(() => this.createBulkItems(items, recipe, true));
  }

  queryOrCreateRecipe(uid : string, recipe : string) : Promise<any> {
    return new Promise((resolve, reject) => {
      this.queryRecipeByName(uid, recipe)
        .subscribe(results => resolve(results));
    })
    .then(results => {
      if (results)
        return Promise.resolve(results);
      else
        return this.createListRecipe(recipe, false);
    });
  }

  queryRecipeByName(uid : string, recipe : string) : Observable<any> {
    return this.db
      .list(`/recipes/${uid}`, { query : {
        orderByChild : 'name',
        equalTo      : recipe
      }})
      .map(data => !!data[0] ? data[0].$key : null);
  }

  getItemsFromRecipe(recipeID : string) : Observable<string> {
    return this.items
      .take(1)
      .map(lists => {
        for (const list of lists) {
          if (list.$key === recipeID)
            return list;
        }

        return {};
      })
      .map(recipe => {
        let recipeItems : string[] = [];

        for (const ingredient in recipe) {
          const item : Item = recipe[ingredient];

          recipeItems.push([
            item.qty / (item.scale || 1), // return original/unscaled recipe quantity
            item.unit,
            item.item
          ].join(' '));
        }

        return recipeItems
          .join('\n')
          .replace(/\n{2,}/g, '\n')
          .replace(/\s{2,}/g, ' ');
      });
  }

  displayListsRecipes(lists : boolean, sort : string, ascending : boolean = true) : Observable<any[]> {
    return this[lists ? 'lists' : 'recipes']
      .map(this.shunTheDamned)
      .map(data => {
        if (sort === 'name' || sort === 'normalizedName') {
          sort = 'normalizedName';

          for (const listRecipe of data) {
            listRecipe[sort] = listRecipe.name.toLowerCase();
          }
        }

        data = _.sortBy(data, [sort]);

        if (!ascending)
          data.reverse();

        return data;
      });
  }

  // remove items that are scheduled for deletion
  shunTheDamned(data : any[]) : any[] {
    return data.filter(item => !item.deadManWalking);
  }

  bulkUpdateItem(combinedItem : Item, changes : object) : void {
    this.updateItem(combinedItem, changes);

    this.queryListItems({ key : 'combined', value : combinedItem.$key })
      .take(1)
      .subscribe(combinedItems => {
        for (const listItem of combinedItems) {
          this.updateItem(listItem, changes);
        }
      });
  }

  updateItem(listItem : Item, changes) : firebase.Promise<any> {
    // if updating the unit or item, update the parsed info as well
    if (changes.unit != null || changes.item != null) {
      const fauxDescription : Array<any> = [
        changes.qty  || listItem.qty,
        (typeof changes.unit === 'string' ? changes.unit : listItem.unit),
        changes.item || listItem.item
      ];

      _.assign(changes, ParseDescription(fauxDescription.join(' ').replace(/\s{2,}/g, ' ')));
    }

    // check if unitType has changed and if item no longer belongs with a group
    const newUnitType = !!changes.unitType && listItem.unitType !== changes.unitType,
      deGroup = !!listItem.combined && newUnitType;

    if (deGroup)
      changes.combined = null;

    // update updated timestamp
    changes.lastUpdated = firebase.database.ServerValue.TIMESTAMP;

    return this.listItems.update(listItem.$key, changes)
      .then(() => {
        if (newUnitType)
          this.combineLikeItems();

        if (deGroup)
          this.removeItemFromCombinedItems(_.assign(listItem, changes, { combined : listItem.combined }));

        return Promise.resolve(true);
      });
  }

  removeItem(item : Item, itemID? : string) : firebase.Promise<any> {
    const key = item ? item.$key : itemID;

    if (key == null || key === '' || typeof key !== 'string')
      return Promise.resolve(false);

    return this.listItems.remove(key)
      .then(() => {
        if (item && item.combined)
          this.removeItemFromCombinedItems(item);

        return Promise.resolve(key);
      });
  }

  removeItemFromCombinedItems(itemToRemove : Item) : void {
    this.queryListItems({ key : 'combined', value : itemToRemove.combined })
      .take(1)
      .subscribe(combinedItems => {
        if (combinedItems.length === 1)
          this.removeCombinedItem(combinedItems[0]);
        else {
          this.decrementCombinedItem(itemToRemove);
        }
      });
  }

  removeCombinedItem(itemToRemove) : void {
    this.updateItem(itemToRemove, { combined : null })
      .then(() => {
        this.removeItem(null, itemToRemove.combined);
      });
  }

  decrementCombinedItem(itemToRemove : Item) : void {
    this.queryListItems({ key : '$key', value : itemToRemove.combined })
      .take(1)
      .subscribe(combinedItem => {
        const qty = itemToRemove.unitType === 'quantity' ? itemToRemove.qty : this.convertMeasurement(
          itemToRemove.unit,
          combinedItem[0].unit,
          itemToRemove.qty
        );

        this.updateItem(combinedItem[0], {
          qty : combinedItem[0].qty - qty
        });
      });
  }

  scaleRecipe(scaleBy : number, group) {
    for (const item of group) {
      this.updateItem(item, {
        scale : scaleBy,
        qty   : item.qty / (item.scale || 1) * scaleBy
      });
    }
  }

  convertMeasurement(fromUnit, toUnit, oldQty) : number {
    const conversionTable = Conversions[MeasurementTypes[fromUnit]],
      newQty = oldQty / conversionTable[fromUnit] * conversionTable[toUnit];

    return isNaN(newQty) ? oldQty : newQty;
  }

  queryListItems(queryObject) : Observable<any> {
    return this.listItems
      .take(1)
      .map(listItems => {
        let results : Item[] = [];

        for (const listItem of listItems) {
          if (listItem[queryObject.key] === queryObject.value)
            results.push(listItem);
        }

        return results;
      });
  }


  // feedback services
  feedback = {
    get : () : FirebaseListObservable<any[]> => {
      return this.db.list(`/feedback`);
    },
    create : (feedbackObj) : firebase.Promise<any> => {
      const timestamp : object = firebase.database.ServerValue.TIMESTAMP,
        feedback : Feedback = {
          createdAt   : timestamp,
          lastUpdated : timestamp,
          responded   : false,
          resolved    : false,
          email       : feedbackObj.email,
          url         : feedbackObj.url,
          type        : feedbackObj.type,
          subject     : feedbackObj.subject,
          message     : feedbackObj.message,
          uid         : feedbackObj.uid  || null,
          name        : feedbackObj.name || null
        };

      return this.db.list(`/feedback`)
        .push(feedback);
    },
    update : (feedback : Feedback, changes) : firebase.Promise<any> => {
      if (!feedback.lastUpdated)
        feedback.lastUpdated = firebase.database.ServerValue.TIMESTAMP;

      return this.db.list(`/feedback`)
        .update(feedback.$key, changes);
    }
  };


  // Admin services
  admin = {
    update : {
      staleUGC : () => {
        const toClean : string[] = [
          'items',
          'lists',
          'recipes',
          'sorting'
        ];

        console.log('grabbing list of UIDs');

        this.db.list('/users')
          .take(1)
          .subscribe(users => {
            let UIDs : string[] = [];

            for (const user of users) {
              UIDs.push(user.$key);
            }

            console.log(`UID list assembled\n${users.length} total users`);

            for (const section of toClean) {
              this.db.list(`/${section}`)
                .take(1)
                .subscribe(items => {
                  console.log(`- cleaning section: '${section}'`);

                  for (const item of items) {
                    if (UIDs.indexOf(item.$key) === -1) {
                      console.log(`  - removing item: '/${section}/${item.$key}'`);

                      this.db.object(`/${section}/${item.$key}`).remove();
                    }
                  }
                });
            }
          });
      },

      lowercaseItems : () => {
        this.db.list('/items')
          .take(1)
          .subscribe(users => {
            let updateCount : number = 0;

            console.log('iterating through items');

            for (const user of users) {
              console.log(`- scanning user: '${user.$key}'`);

              for (const list in user) {
                console.log(`  - scanning list: '${list}'`);

                for (const item in user[list]) {
                  if (user[list][item].lowercaseItem == null) {
                    console.log(`    - adding lowercaseItem entry to item: '${item}'`);

                    updateCount++;

                    this.db.object(`/items/${user.$key}/${list}/${item}`).update({
                      lastUpdated   : firebase.database.ServerValue.TIMESTAMP,
                      lowercaseItem : user[list][item].item.toLowerCase()
                    });
                  }
                }
              }
            }

            console.log(`updated ${updateCount} item/s`);
          });
      },

      itemDept : (forceReclassify : boolean = false) => {
        this.db.list('/items')
          .take(1)
          .subscribe(users => {
            const timestamp = firebase.database.ServerValue.TIMESTAMP;

            let items        : number = 0,
              updateCount    : number = 0,
              unchangedCount : number = 0,
              unknownCount   : number = 0,
              unknownItems   : {}     = {};

            console.log('iterating through items');

            for (const user of users) {
              console.log(`- scanning user: '${user.$key}'`);

              for (const list in user) {
                console.log(`  - scanning list: '${list}'`);

                for (const item in user[list]) {
                  items++;

                  if (user[list][item].dept == null || forceReclassify) {
                    console.log(`    - attempting to reclassify item: '${item}'`);

                    ParseDept(user[list][item].item.split(' '), dept => {
                      if (user[list][item].dept != dept) {
                        console.log(`      - new department identified: '${dept}'`);

                        updateCount++;

                        this.db.object(`/items/${user.$key}/${list}/${item}`).update({
                          lastUpdated : firebase.database.ServerValue.TIMESTAMP,
                          dept
                        });
                      } else if (user[list][item].dept != null) {
                        console.log(`      - no change in dept: '${dept}'`);

                        unchangedCount++;
                      } else {
                        console.log(`      - adding item to unknown list: '${user[list][item].lowercaseItem}'`);

                        unknownCount++;

                        unknownItems[user[list][item].lowercaseItem] = true;
                      }
                    });
                  } else if (user[list][item].dept != null) {
                    unchangedCount++;
                  }
                }
              }
            }

            console.log(`reviewed item/s: ${items}`);

            console.log(`  - updated   item/s: ${updateCount}`);

            console.log(`  - unknown   item/s: ${unknownCount}`);

            console.log(`  - unchanged item/s: ${unchangedCount}`);

            // create/update list of unknown items
            this.db.object(`/admin/unknownItems`)
              .set(unknownItems);
          });
      }
    }
  };
}


// WEBPACK FOOTER //
// /Users/briznad/Documents/WebDev/personal/cartmigo/cartmigo-beta/src/app/services/firebase.service.ts